/**
 * Extra actions (rulebook p.11, R-012 / R-023): after performing an action, a
 * player may discard one extra-action tile showing that action to perform it
 * again; a "+2" marker on that action's space lets them perform it twice. A
 * wild-extra-action forum tile stands in for a tile of any performed action.
 * The repeated action itself then runs through the ordinary awaitAction step.
 */
import { fail, playerOf, succeed, updatePlayer } from './internal.js';
import { MANIFESTS } from './manifests.js';
export function extraActionOptions(state, player) {
    if (state.turn.extraActionUsed)
        return [];
    const performed = [...new Set(state.turn.performed)];
    const out = [];
    for (const tile of player.extraActionTiles) {
        if (performed.includes(tile.action))
            out.push({ type: 'UseExtraActionTile', tileId: tile.id });
    }
    for (const tile of player.forumTiles) {
        if (tile.kind !== 'wild-extra-action')
            continue;
        for (const action of performed)
            out.push({ type: 'UseExtraActionTile', tileId: tile.id, action });
    }
    return out;
}
export function useExtraActionTile(state, actor, tileId, action) {
    if (state.phase.kind !== 'awaitExtraAction')
        return fail('WRONG_PHASE', 'not the extra-action step');
    if (state.turn.extraActionUsed) {
        return fail('ILLEGAL', `only ${MANIFESTS.maxExtraActionsPerTurn} extra action per turn`);
    }
    const player = playerOf(state, actor);
    const extraTile = player.extraActionTiles.find((t) => t.id === tileId);
    const wild = player.forumTiles.find((t) => t.id === tileId && t.kind === 'wild-extra-action');
    if (!extraTile && !wild)
        return fail('ILLEGAL', 'no such extra-action tile on your mat');
    const repeat = extraTile ? extraTile.action : action;
    if (!repeat)
        return fail('ILLEGAL', 'a wild extra-action tile needs the action to repeat');
    if (!state.turn.performed.includes(repeat)) {
        return fail('ILLEGAL', `you did not perform the ${repeat} action this turn`);
    }
    const times = player.plus2Spaces.includes(repeat) ? 2 : 1;
    const pending = Array.from({ length: times }, () => ({
        source: 'extraTile',
        action: repeat,
        tileId,
    }));
    let next = updatePlayer(state, actor, (pl) => ({
        ...pl,
        extraActionTiles: pl.extraActionTiles.filter((t) => t.id !== tileId),
        forumTiles: pl.forumTiles.filter((t) => t.id !== tileId),
    }));
    next = {
        ...next,
        removed: {
            ...next.removed,
            extraAction: extraTile ? [...next.removed.extraAction, extraTile] : next.removed.extraAction,
            forum: wild ? [...next.removed.forum, wild] : next.removed.forum,
        },
        turn: { ...next.turn, extraActionUsed: true },
        pendingActions: pending,
        phase: { kind: 'awaitAction', action: repeat },
    };
    return succeed(next, [{ type: 'ExtraActionUsed', player: actor, tileId, action: repeat, times }]);
}
//# sourceMappingURL=extraActions.js.map